import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { ContentIcon, CONTENT_ICON_OPTIONS } from './icons';
import {
  ContentButton,
  ContentCounter,
  ContentLink,
  ContentPrice,
  isValidContentLink,
} from './templates';

const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

export const ContenidoSurfaceContext = createContext<'edit' | 'preview' | 'live'>('preview');

function readFile(file: File, onLoad: (value: string) => void) {
  if (!file.type.startsWith('image/')) return;
  if (file.size > MAX_IMAGE_BYTES) {
    window.alert('La imagen no puede superar 5 MB');
    return;
  }
  const reader = new FileReader();
  reader.onload = () => {
    if (typeof reader.result === 'string') onLoad(reader.result);
  };
  reader.readAsDataURL(file);
}

export function PlainText({
  value,
  placeholder,
  className,
  editing,
  chip,
  path,
  anim,
  delay = 0,
  onChange,
}: {
  value: string;
  placeholder: string;
  className?: string;
  editing: boolean;
  chip?: string;
  path: string;
  anim?: string;
  delay?: number;
  onChange: (value: string) => void;
}) {
  if (!editing && !value.trim()) return null;
  return (
    <div
      className={anim ? 'ed-field anim' : 'ed-field'}
      data-anim={anim}
      data-path={path}
      style={anim ? { ['--d' as string]: `${delay}ms` } : undefined}
    >
      {editing && chip ? (
        <button
          className='chip'
          onClick={(event) => {
            const node =
              event.currentTarget.parentElement?.querySelector<HTMLElement>('[data-edit]');
            if (!node) return;
            node.focus();
            const selection = window.getSelection();
            selection?.selectAllChildren(node);
            selection?.collapseToEnd();
          }}
          type='button'
        >
          {chip}
          <PenIcon />
        </button>
      ) : null}
      <EditableText
        className={className}
        editing={editing}
        onChange={onChange}
        placeholder={placeholder}
        value={value}
      />
    </div>
  );
}

function PenIcon() {
  return (
    <svg aria-hidden='true' className='ed-pen' viewBox='0 0 24 24'>
      <path d='M12 20h9' />
      <path d='M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z' />
    </svg>
  );
}

function UploadIcon() {
  return (
    <svg aria-hidden='true' viewBox='0 0 24 24'>
      <rect height='18' rx='4' width='18' x='3' y='3' />
      <path d='M12 16V8M8.5 11.5L12 8l3.5 3.5' />
    </svg>
  );
}

function ArrowIcon() {
  return (
    <svg aria-hidden='true' viewBox='0 0 34 16'>
      <path d='M1 8h31M25 1l7 7-7 7' />
    </svg>
  );
}

function EditableText({
  value,
  className,
  editing,
  placeholder,
  onChange,
}: {
  value: string;
  className?: string;
  editing: boolean;
  placeholder: string;
  onChange: (value: string) => void;
}) {
  const nodeRef = useRef<HTMLDivElement>(null);
  const focusedRef = useRef(false);
  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node || focusedRef.current) return;
    if ((node.textContent || '') !== value) node.textContent = value;
  }, [value]);

  return (
    <div
      className={className}
      contentEditable={editing}
      data-edit={editing ? 'true' : undefined}
      data-ph={placeholder}
      onBlur={(event) => {
        focusedRef.current = false;
        onChange(event.currentTarget.textContent || '');
      }}
      onFocus={() => {
        focusedRef.current = true;
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') event.preventDefault();
      }}
      onPaste={(event) => {
        event.preventDefault();
        const text = event.clipboardData.getData('text/plain').replace(/\s+/g, ' ').trim();
        document.execCommand('insertText', false, text);
      }}
      ref={nodeRef}
      suppressContentEditableWarning
    />
  );
}

export function Picture({
  value,
  className,
  editing,
  path,
  background,
  anim,
  delay = 0,
  onChange,
  children,
}: {
  value: string;
  className?: string;
  editing: boolean;
  path: string;
  background?: boolean;
  anim?: string;
  delay?: number;
  onChange: (value: string) => void;
  children?: React.ReactNode;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const image = value ? { backgroundImage: `url("${value}")` } : {};
  const style = {
    ...(background ? {} : image),
    ...(anim ? { ['--d' as string]: `${delay}ms` } : {}),
  };
  const zoneClass =
    `${background ? 'bg-zone' : 'z-img'} ${className || ''} ${anim ? 'anim' : ''} ${value ? '' : 'is-empty'}`.trim();

  function take(file?: File) {
    if (file) readFile(file, onChange);
  }

  return (
    <div
      className={zoneClass}
      data-anim={anim}
      data-path={path}
      onClick={(event) => {
        if (!editing || value || background) return;
        const target = event.target as HTMLElement;
        const blocked = target.closest(
          '.t-counter, .t-icon, .t-slide, .z-img, .btn-wrap, button, a, input, textarea, [contenteditable="true"]',
        );
        if (blocked && blocked !== event.currentTarget && event.currentTarget.contains(blocked))
          return;
        inputRef.current?.click();
      }}
      onDragOver={(event) => editing && event.preventDefault()}
      onDrop={(event) => {
        if (!editing) return;
        event.preventDefault();
        take(event.dataTransfer.files?.[0]);
      }}
      style={style}
    >
      {background ? (
        <>
          <div className='bg-layer' style={image} />
          <div className='bg-shade' />
        </>
      ) : null}
      {editing ? (
        <>
          <span className='z-img-ph'>
            {background
              ? 'Imagen de fondo: arrastrala o usá el ícono'
              : 'Arrastrá una imagen o hacé clic'}
          </span>
          <button
            aria-label={background ? 'Cambiar imagen de fondo' : 'Reemplazar imagen'}
            className='z-replace'
            onClick={(event) => {
              event.stopPropagation();
              inputRef.current?.click();
            }}
            title={background ? 'Cambiar imagen de fondo' : 'Reemplazar imagen'}
            type='button'
          >
            <UploadIcon />
          </button>
          <input
            accept='image/png,image/jpeg,image/webp,image/gif'
            hidden
            onChange={(event) => {
              take(event.target.files?.[0]);
              event.target.value = '';
            }}
            ref={inputRef}
            type='file'
          />
        </>
      ) : null}
      {children}
    </div>
  );
}

function Popover({
  title,
  onCancel,
  onApply,
  actions = true,
  children,
}: {
  title: string;
  onCancel: () => void;
  onApply: () => void;
  actions?: boolean;
  children: React.ReactNode;
}) {
  const probeRef = useRef<HTMLDivElement>(null);
  const [place, setPlace] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onCancel();
      if (event.key === 'Enter') {
        event.preventDefault();
        onApply();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onApply, onCancel]);

  useLayoutEffect(() => {
    const anchor = probeRef.current?.parentElement;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const width = 320;
    const height = 300;
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - width - 12));
    let top = rect.bottom + 8;
    if (top + height > window.innerHeight - 10 && rect.top - height - 8 > 10) {
      top = rect.top - height - 8;
    }
    top = Math.max(10, top);
    setPlace({ top, left });
  }, []);

  if (!place) return <div ref={probeRef} />;

  return createPortal(
    <div className='contenido-block' style={{ display: 'contents' }}>
      <div
        aria-label={title}
        className='pop'
        role='dialog'
        style={{ position: 'fixed', top: place.top, left: place.left, zIndex: 120 }}
      >
        <div className='pop-head'>
          {title}
          <button aria-label='Cerrar' className='pop-x' onClick={onCancel} type='button'>
            ✕
          </button>
        </div>
        {children}
        {actions ? (
          <div className='pop-actions'>
            <button className='pop-btn pop-btn-primary' onClick={onApply} type='button'>
              Aplicar
            </button>
            <button className='pop-btn' onClick={onCancel} type='button'>
              Cancelar
            </button>
          </div>
        ) : null}
      </div>
    </div>,
    document.body,
  );
}

export function ActionButton({
  value,
  editing,
  path,
  light,
  filled,
  onChange,
  onNotice,
}: {
  value: ContentButton;
  editing: boolean;
  path: string;
  light?: boolean;
  filled?: boolean;
  onChange: (value: ContentButton) => void;
  onNotice?: (message: string) => void;
}) {
  const surface = useContext(ContenidoSurfaceContext);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  if (!editing && !value.texto.trim()) return null;

  function apply() {
    if (draft.texto.trim() && !isValidContentLink(draft.link.trim())) {
      onNotice?.('El link debe ser https://, una ruta /, # o mailto:');
      return;
    }
    onChange({ ...draft, texto: draft.texto.trim(), link: draft.link.trim() });
    setOpen(false);
  }

  function openEditor() {
    setDraft(value);
    setOpen(true);
  }

  const className = ['t-btn', light ? 'light' : '', filled ? 'filled' : '']
    .filter(Boolean)
    .join(' ');
  return (
    <span className={value.texto.trim() ? 'btn-wrap' : 'btn-wrap is-blank'} data-path={path}>
      <a
        className={className}
        href={value.link || '#'}
        onClick={(event) => {
          if (editing) {
            event.preventDefault();
            openEditor();
            return;
          }
          if (surface === 'live' && value.link) return;
          event.preventDefault();
          if (value.link) onNotice?.(`Link: ${value.link}`);
        }}
        rel={!editing && surface === 'live' && value.nueva ? 'noopener noreferrer' : undefined}
        target={!editing && surface === 'live' && value.nueva ? '_blank' : undefined}
      >
        {value.texto || <span className='ph'>Nombre del botón</span>}
      </a>
      {editing ? (
        <button className='chip' onClick={openEditor} type='button'>
          Botón
          <PenIcon />
        </button>
      ) : null}
      {open ? (
        <Popover onApply={apply} onCancel={() => setOpen(false)} title='Botón'>
          <label className='pf'>
            <span className='field-label'>Nombre del botón</span>
            <input
              className='text-input'
              onChange={(event) => setDraft({ ...draft, texto: event.target.value })}
              placeholder='Ej: Conocé más'
              value={draft.texto}
            />
          </label>
          <label className='pf'>
            <span className='field-label'>Link</span>
            <input
              className='text-input'
              onChange={(event) => setDraft({ ...draft, link: event.target.value })}
              placeholder='https://… o /ruta'
              value={draft.link}
            />
          </label>
          <label className='pop-check'>
            <input
              checked={draft.nueva}
              onChange={(event) => setDraft({ ...draft, nueva: event.target.checked })}
              type='checkbox'
            />
            Abrir en pestaña nueva
          </label>
        </Popover>
      ) : null}
    </span>
  );
}

export function ArrowLink({
  value,
  editing,
  path,
  label,
  onChange,
  onNotice,
}: {
  value: ContentLink;
  editing: boolean;
  path: string;
  label?: string;
  onChange: (value: ContentLink) => void;
  onNotice?: (message: string) => void;
}) {
  const surface = useContext(ContenidoSurfaceContext);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  if (!editing && !value.link.trim() && !value.texto.trim()) return null;
  if (!editing && surface === 'live') {
    const text = label ? value.texto || label : <ArrowIcon />;
    if (!value.link.trim()) return <span className={label ? 't-more' : 't-arrow'}>{text}</span>;
    return (
      <a className={label ? 't-more' : 't-arrow'} href={value.link}>
        {text}
      </a>
    );
  }

  function apply() {
    if (draft.link.trim() && !isValidContentLink(draft.link.trim())) {
      onNotice?.('El link debe ser https://, una ruta /, # o mailto:');
      return;
    }
    onChange({ texto: draft.texto.trim(), link: draft.link.trim() });
    setOpen(false);
  }

  return (
    <span className='btn-wrap' data-path={path}>
      <button
        className={label ? 't-more' : 't-arrow'}
        onClick={() => {
          if (editing) {
            setDraft(value);
            setOpen(true);
            return;
          }
          if (value.link) onNotice?.(`Link: ${value.link}`);
        }}
        type='button'
      >
        {label ? value.texto || label : <ArrowIcon />}
      </button>
      {open ? (
        <Popover onApply={apply} onCancel={() => setOpen(false)} title='Link'>
          {label ? (
            <label className='pf'>
              <span className='field-label'>Texto del link</span>
              <input
                className='text-input'
                onChange={(event) => setDraft({ ...draft, texto: event.target.value })}
                placeholder='Ej: Más'
                value={draft.texto}
              />
            </label>
          ) : null}
          <label className='pf'>
            <span className='field-label'>Link</span>
            <input
              className='text-input'
              onChange={(event) => setDraft({ ...draft, link: event.target.value })}
              placeholder='https://… o /ruta'
              value={draft.link}
            />
          </label>
        </Popover>
      ) : null}
    </span>
  );
}

export function WordChoice({
  value,
  image,
  editing,
  onChange,
  onNotice,
}: {
  value: string;
  image: string;
  editing: boolean;
  onChange: (value: string) => void;
  onNotice?: (message: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(value);
  if (!editing && !value.trim()) return null;

  function apply() {
    const word = draft.trim().toUpperCase();
    if (!word) {
      onNotice?.('Ingresá una palabra');
      return;
    }
    if (word.length > 10) {
      onNotice?.('Usá hasta 10 letras para que entre en una línea');
      return;
    }
    onChange(word);
    setOpen(false);
  }

  return (
    <div
      className='ed-word anim'
      data-anim='track'
      data-path='palabra'
      onClick={(event) => {
        if (!editing || open || !event.currentTarget.contains(event.target as Node)) return;
        setDraft(value);
        setOpen(true);
      }}
    >
      <div className='ed-word-a'>{value || <span className='ph'>PALABRA</span>}</div>
      <div
        aria-hidden='true'
        className={image ? 'ed-word-b' : 'ed-word-b noimg'}
        style={image ? { backgroundImage: `url("${image}")` } : undefined}
      >
        {value}
      </div>
      {editing ? (
        <span className='chip ed-word-chip'>
          Palabra
          <PenIcon />
        </span>
      ) : null}
      {open ? (
        <Popover onApply={apply} onCancel={() => setOpen(false)} title='Palabra destacada'>
          <label className='pf'>
            <span className='field-label'>Palabra (máx. 10 letras)</span>
            <input
              autoFocus
              className='text-input'
              onChange={(event) => setDraft(event.target.value)}
              placeholder='Ej: EXPLORA'
              value={draft}
            />
          </label>
        </Popover>
      ) : null}
    </div>
  );
}

export function IconChoice({
  value,
  editing,
  path,
  solid,
  onChange,
}: {
  value: string;
  editing: boolean;
  path: string;
  solid?: boolean;
  onChange: (value: string) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <span className='icon-wrap' data-path={path}>
      <button
        className={solid ? 't-icon solid' : 't-icon'}
        disabled={!editing}
        onClick={() => editing && setOpen(true)}
        type='button'
      >
        <ContentIcon id={value} />
      </button>
      {open ? (
        <Popover
          actions={false}
          onApply={() => setOpen(false)}
          onCancel={() => setOpen(false)}
          title='Elegí un ícono'
        >
          <div className='ico-grid'>
            {CONTENT_ICON_OPTIONS.map((option) => (
              <button
                className={option.id === value ? 'ico-opt on' : 'ico-opt'}
                key={option.id}
                onClick={() => {
                  onChange(option.id);
                  setOpen(false);
                }}
                type='button'
              >
                <ContentIcon id={option.id} />
                {option.label}
              </button>
            ))}
          </div>
        </Popover>
      ) : null}
    </span>
  );
}

function formatCount(value: number, suffix: string) {
  const formatted = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 }).format(value);
  return `${formatted}${suffix || ''}`;
}

export function Counter({
  value,
  editing,
  path,
  animate,
  replayToken,
  delay = 0,
  onChange,
  onNotice,
}: {
  value: ContentCounter;
  editing: boolean;
  path: string;
  animate: boolean;
  replayToken: number;
  delay?: number;
  onChange: (value: ContentCounter) => void;
  onNotice?: (message: string) => void;
}) {
  const [shown, setShown] = useState(animate && !editing ? 0 : value.valor);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ ...value, valor: String(value.valor) });

  useEffect(() => {
    if (editing || !animate) {
      setShown(value.valor);
      return undefined;
    }
    const started = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - started) / 1700);
      setShown(value.valor * progress);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [animate, editing, replayToken, value.valor]);

  function apply() {
    const parsed = Number(draft.valor.replace(',', '.'));
    if (draft.valor.trim() === '' || !Number.isFinite(parsed) || parsed < 0) {
      onNotice?.('Ingresá un número mayor o igual a 0');
      return;
    }
    onChange({ valor: parsed, sufijo: draft.sufijo.trim(), etiqueta: draft.etiqueta.trim() });
    setOpen(false);
  }

  return (
    <div
      className='t-counter anim'
      data-anim='zoom'
      data-path={path}
      onClick={(event) => {
        if (!editing || open || !event.currentTarget.contains(event.target as Node)) return;
        setDraft({ ...value, valor: String(value.valor) });
        setOpen(true);
      }}
      style={{ ['--d' as string]: `${delay}ms` }}
    >
      <div className='t-counter-circle'>{formatCount(shown, value.sufijo)}</div>
      {value.etiqueta || editing ? (
        <div className='t-counter-label'>
          {value.etiqueta || <span className='ph'>Etiqueta</span>}
        </div>
      ) : null}
      {editing ? (
        <span className='chip chip-mini'>
          <PenIcon />
          Editar
        </span>
      ) : null}
      {open ? (
        <Popover onApply={apply} onCancel={() => setOpen(false)} title='Contador'>
          <label className='pf'>
            <span className='field-label'>Valor</span>
            <input
              className='text-input'
              inputMode='decimal'
              onChange={(event) => setDraft({ ...draft, valor: event.target.value })}
              placeholder='Ej: 680'
              value={draft.valor}
            />
          </label>
          <label className='pf'>
            <span className='field-label'>Sufijo (opcional)</span>
            <input
              className='text-input'
              maxLength={8}
              onChange={(event) => setDraft({ ...draft, sufijo: event.target.value })}
              placeholder='Ej: k, m, +'
              value={draft.sufijo}
            />
          </label>
          <label className='pf'>
            <span className='field-label'>Etiqueta</span>
            <input
              className='text-input'
              onChange={(event) => setDraft({ ...draft, etiqueta: event.target.value })}
              placeholder='Ej: Destinos activos'
              value={draft.etiqueta}
            />
          </label>
        </Popover>
      ) : null}
    </div>
  );
}

function decimalsOf(value: number) {
  return (String(value).split('.')[1] || '').length;
}

export function Price({
  value,
  editing,
  path,
  animate,
  replayToken,
  onChange,
  onNotice,
}: {
  value: ContentPrice;
  editing: boolean;
  path: string;
  animate: boolean;
  replayToken: number;
  onChange: (value: ContentPrice) => void;
  onNotice?: (message: string) => void;
}) {
  const [shown, setShown] = useState(animate && !editing ? 0 : value.valor);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ ...value, valor: String(value.valor) });

  useEffect(() => {
    if (editing || !animate) {
      setShown(value.valor);
      return undefined;
    }
    const started = performance.now();
    let frame = 0;
    const tick = (now: number) => {
      const progress = Math.min(1, (now - started) / 1700);
      setShown(value.valor * progress);
      if (progress < 1) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [animate, editing, replayToken, value.valor]);

  function apply() {
    const parsed = Number(draft.valor.replace(',', '.'));
    if (draft.valor.trim() === '' || !Number.isFinite(parsed) || parsed < 0) {
      onNotice?.('Ingresá un número mayor o igual a 0');
      return;
    }
    onChange({ moneda: draft.moneda.trim(), valor: parsed, nota: draft.nota.trim() });
    setOpen(false);
  }

  const amount = new Intl.NumberFormat('es-AR', {
    maximumFractionDigits: decimalsOf(value.valor),
  }).format(shown);

  return (
    <div className='t-price-wrap' data-path={path}>
      <button
        className='t-price'
        disabled={!editing}
        onClick={() => {
          if (!editing) return;
          setDraft({ ...value, valor: String(value.valor) });
          setOpen(true);
        }}
        type='button'
      >
        <span className='t-price-lbl'>Desde</span>
        <span className='t-price-num'>{value.moneda ? `${value.moneda} ${amount}` : amount}</span>
        {value.nota ? (
          <span className='t-price-note'>{value.nota}</span>
        ) : editing ? (
          <span className='t-price-note ph'>Nota del precio</span>
        ) : null}
        {editing ? (
          <span className='chip chip-mini'>
            <PenIcon />
            Editar precio
          </span>
        ) : null}
      </button>
      {open ? (
        <Popover onApply={apply} onCancel={() => setOpen(false)} title='Precio «desde»'>
          <label className='pf'>
            <span className='field-label'>Moneda</span>
            <input
              className='text-input'
              maxLength={8}
              onChange={(event) => setDraft({ ...draft, moneda: event.target.value })}
              placeholder='Ej: USD, ARS, EUR'
              value={draft.moneda}
            />
          </label>
          <label className='pf'>
            <span className='field-label'>Valor</span>
            <input
              className='text-input'
              inputMode='decimal'
              onChange={(event) => setDraft({ ...draft, valor: event.target.value })}
              placeholder='Ej: 1890'
              value={draft.valor}
            />
          </label>
          <label className='pf'>
            <span className='field-label'>Nota</span>
            <input
              className='text-input'
              onChange={(event) => setDraft({ ...draft, nota: event.target.value })}
              placeholder='Ej: por persona en base doble'
              value={draft.nota}
            />
          </label>
        </Popover>
      ) : null}
    </div>
  );
}
